import { createFileRoute } from "@tanstack/react-router";
import React, { useEffect } from "react";
import {
  ActivityIcon,
  BoxesIcon,
  CheckIcon,
  ChevronUpIcon,
  EyeOffIcon,
  SunIcon,
} from "lucide-react";
import { useMap, useSet } from "@uidotdev/usehooks";
import { useQuery } from "@tanstack/react-query";
import { useCommandScore } from "use-command-score";
import type { Policy } from "@/lib/queries/governance/policies";
import { useState } from "@/lib/hooks/mocks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { governancePoliciesQueryOptions } from "@/lib/queries/governance/policies";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { governanceEntitiesQueryOptions } from "@/lib/queries/governance/entities";
import { LoadingSpinner } from "@/components/loading";
import { FilePreview } from "@/components/filePreview";

import highlightedPdf from "@/assets/pdf/insurance-highlighted.pdf?url";
import maskedPdf from "@/assets/pdf/insurance-masked.pdf?url";

export const Route = createFileRoute("/_app/document-redactor/options")({
  component: RouteComponent,

  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(governancePoliciesQueryOptions);
    queryClient.ensureQueryData(governanceEntitiesQueryOptions);
  },
});

type RedactionType = "highlight" | "redact";
type RedactionScope = "regular" | "contextual";

function RouteComponent() {
  const entities = useQuery({
    ...governanceEntitiesQueryOptions,
    select(data) {
      return new Set(data.entity_list);
    },
  });

  const selectedEntities = useSet<string>();
  const selectedPolicies = useMap<string, Policy>([
    [
      "HIPAA",
      {
        name: "HIPAA",
        description:
          "HIPAA compliance redaction policy using backend recognized entities.",
        entities: [
          "FIRSTNAME",
          "MIDDLENAME",
          "LASTNAME",
          "STREET",
          "Address",
          "BUILDINGNUMBER",
          "SECONDARYADDRESS",
          "CITY",
          "COUNTY",
          "ZIPCODE",
          "TIMEDATE",
          "PHONENUMBER",
          "EMAIL",
          "SSN",
          "ACCOUNTNUMBER",
          "IDCARD",
          "VEHICLEIDENTIFIERS",
          "DEVICEIDENTIFIERS",
          "URL",
          "MASKEDSEQUENCE",
        ],
        entities_aggregated: [
          "Social Security Number",
          "Vehicle Identifier",
          "URL",
          "Zipcode",
          "Phone Number",
          "Date/Time",
          "Device Identifier",
          "Location",
          "Masked Number",
          "Email",
          "SSN",
          "Person",
          "Account Number",
          "Address",
        ],
      },
    ],
  ]);

  useEffect(() => {
    if (entities.isSuccess) {
      selectedEntities.clear();
      entities.data.values().forEach(selectedEntities.add);
    }
  }, [entities.isSuccess]);

  const policyEntities = new Set(
    selectedPolicies.values().flatMap((p) => p.entities_aggregated),
  );

  const [redactionType, setRedactionType] =
    React.useState<RedactionType>("redact");
  const fileUrl = redactionType === "highlight" ? highlightedPdf : maskedPdf;

  const [redactionScope, setRedactionScope] =
    useState<RedactionScope>("contextual");

  const [searchEntities, setSearchEntities] = React.useState("");
  const [showMore, setShowMore] = React.useState(false);
  const foundEntities = useCommandScore(searchEntities, [
    ...(entities.data?.values() ?? []),
  ]);
  const hasMoreEntities = foundEntities.length > 10;

  const entitiesToShow = showMore ? foundEntities : foundEntities.slice(0, 10);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row">
      <div className="flex-1">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Document Redactor</h1>
            <p className="text-muted-foreground">
              Highlights PIIs on your files
            </p>
          </div>
        </div>

        <Card className="relative flex h-90 w-full items-center justify-center overflow-clip border-dashed bg-foreground/10 p-2 has-data-[previewing]:h-180">
          <FilePreview file_url={fileUrl} />
        </Card>
      </div>

      <div className="w-full lg:w-[400px]">
        <h2 className="mb-6 text-xl font-semibold">
          Configure redaction settings
        </h2>

        <div className="space-y-6">
          <div>
            <label className="mb-2 block font-medium">
              Redaction type<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionType === "highlight" ? "default" : "outline"}
                onClick={() => setRedactionType("highlight")}
              >
                <SunIcon className="h-4 w-4" />
                Highlight PIIs
              </Button>
              <Button
                className="flex-1 justify-start gap-2"
                variant={redactionType === "redact" ? "default" : "outline"}
                onClick={() => setRedactionType("redact")}
              >
                <EyeOffIcon className="h-4 w-4" />
                Mask PIIs
              </Button>
            </div>
          </div>
          <div>
            <label className="mb-2 block font-medium">
              Redaction scope<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <Button
                disabled
                className="flex-1 justify-start gap-2"
                variant={redactionScope === "regular" ? "default" : "outline"}
                onClick={() => setRedactionScope("regular")}
              >
                <ActivityIcon className="h-4 w-4" />
                Regular
              </Button>
              <Button
                disabled
                className="flex-1 justify-start gap-2"
                variant={
                  redactionScope === "contextual" ? "default" : "outline"
                }
                onClick={() => setRedactionScope("contextual")}
              >
                <BoxesIcon className="h-4 w-4" />
                Contextual
              </Button>
            </div>
          </div>

          <div>
            <label className="mb-2 block font-medium">
              Select PIIs<span className="text-red-500">*</span>
            </label>

            <PolicyCombobox
              selectedPolicies={selectedPolicies}
              onPolicySelect={(k, p) =>
                selectedPolicies.has(k)
                  ? selectedPolicies.delete(k)
                  : selectedPolicies.set(k, p)
              }
            />

            <Input
              value={searchEntities}
              onChange={(e) => {
                setSearchEntities(e.target.value);
                setShowMore(false);
              }}
              type="search"
              placeholder="Search"
              className="my-2"
            />

            {entities.isSuccess ? (
              <>
                <div className="mt-4 flex flex-wrap gap-2">
                  {entitiesToShow.map((pii) => (
                    <Button
                      key={pii}
                      variant="outline"
                      size="sm"
                      data-forced={policyEntities.has(pii) ? "true" : undefined}
                      data-selected={
                        selectedEntities.has(pii) ? "true" : undefined
                      }
                      className={cn(
                        "rounded-full text-sm",
                        "data-forced:bg-primary data-forced:text-white",
                        "data-selected:bg-gray-900 data-selected:text-white",
                        "dark:data-selected:bg-gray-100 dark:data-selected:text-black",
                        "dark:data-forced:bg-pink-500 dark:data-forced:text-white dark:data-forced:data-selected:bg-pink-500 dark:data-forced:data-selected:text-white",
                      )}
                    >
                      {pii}
                    </Button>
                  ))}
                </div>

                {hasMoreEntities && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowMore(!showMore)}
                    className="mt-1 px-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {showMore ? "Show Less" : "Show More"}
                  </Button>
                )}
              </>
            ) : entities.isLoading ? (
              <LoadingSpinner className="my-5" />
            ) : (
              "Error!"
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyCombobox({
  selectedPolicies,
  onPolicySelect,
}: {
  selectedPolicies: Map<string, Policy>;
  onPolicySelect: (k: string, p: Policy) => void;
}) {
  const selectedKeys = [...selectedPolicies.keys()];
  const policies = useQuery({
    ...governancePoliciesQueryOptions,
    select: (data) => Object.entries(data),
  });

  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="group/popover-trigger flex w-full justify-between"
        >
          {selectedKeys.length > 0
            ? selectedKeys.map((k) => selectedPolicies.get(k)!.name).join(",")
            : "Select Policies"}
          <ChevronUpIcon className="ml-auto size-4 shrink-0 justify-self-end opacity-50 transition-transform group-aria-expanded/popover-trigger:-rotate-180" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-100 p-0">
        <Command className="p-2">
          <CommandInput placeholder="Search policy..." />
          {policies.isLoading ? (
            <LoadingSpinner />
          ) : policies.isSuccess ? (
            <CommandList>
              <CommandEmpty>No Policy Found.</CommandEmpty>
              <CommandGroup>
                {policies.data.map(([k, policy]) => (
                  <CommandItem
                    className="group/command-item flex w-full"
                    key={k}
                    value={k}
                    data-picked={selectedPolicies.has(k) ? "true" : undefined}
                    keywords={[policy.name, policy.description]}
                    onSelect={() => onPolicySelect(k, policy)}
                  >
                    {policy.name}
                    <CheckIcon className="ml-auto justify-self-end group-not-data-picked/command-item:hidden" />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            "Error!"
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
